/**
 * Vision API 로딩 오버레이 컴포넌트
 *
 * Vision API 처리 중 표시되는 전체 화면 로딩 UI
 * 레트로 픽셀 아트 스타일의 스캔 라인 애니메이션
 *
 * Phase 5 개선사항:
 * - 단계별 진행 표시 (압축 → 업로드 → 분석 → 채점)
 * - 타임아웃 경고 (25초 초과 시)
 * - 취소 버튼
 * - 재시도 횟수 표시
 */

import React, { useEffect, useState } from 'react';
import type { VisionProcessingStage } from '../../types/vision';

/**
 * VisionLoadingOverlay Props
 */
interface VisionLoadingOverlayProps {
  /** 표시 여부 */
  isVisible: boolean;

  /** 로딩 메시지 (기본: 단계에 따라 자동 결정) */
  message?: string;

  /** 현재 처리 단계 (Phase 5) */
  stage?: VisionProcessingStage | null;

  /** 재시도 횟수 (Phase 5) */
  retryCount?: number;

  /** 취소 버튼 클릭 핸들러 (Phase 5) */
  onCancel?: () => void;
}

/**
 * 단계별 메시지 및 아이콘 정의
 */
const STAGE_CONFIG: Record<VisionProcessingStage, { message: string; icon: string; progress: number }> = {
  compressing: {
    message: '이미지 최적화 중...',
    icon: '📦',
    progress: 10,
  },
  uploading: {
    message: '서버로 전송 중...',
    icon: '📤',
    progress: 30,
  },
  analyzing: {
    message: 'AI가 답안을 읽는 중...',
    icon: '🔍',
    progress: 60,
  },
  grading: {
    message: '채점 결과 처리 중...',
    icon: '✏️',
    progress: 90,
  },
};

/** 타임아웃 경고 표시 시간 (ms) */
const TIMEOUT_WARNING_THRESHOLD = 25000; // 25초

/**
 * Vision API 로딩 오버레이
 *
 * @example
 * ```tsx
 * <VisionLoadingOverlay
 *   isVisible={mode === 'processing'}
 *   stage={processingStage}
 *   retryCount={retryCount}
 *   onCancel={handleCancel}
 * />
 * ```
 */
export const VisionLoadingOverlay: React.FC<VisionLoadingOverlayProps> = ({
  isVisible,
  message,
  stage,
  retryCount = 0,
  onCancel,
}) => {
  // 경과 시간 추적 (타임아웃 경고용)
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // 타이머 설정
  useEffect(() => {
    if (!isVisible) {
      setElapsedTime(0);
      setShowTimeoutWarning(false);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setElapsedTime(elapsed);

      if (elapsed >= TIMEOUT_WARNING_THRESHOLD) {
        setShowTimeoutWarning(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible]);

  if (!isVisible) return null;

  // 현재 단계 정보 가져오기
  const currentStage = stage ? STAGE_CONFIG[stage] : null;
  const displayMessage = message || currentStage?.message || 'AI가 답안을 읽는 중...';
  const displayIcon = currentStage?.icon || '📄';
  const progress = currentStage?.progress || 50;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
      <div className="text-center space-y-6 max-w-sm px-4">
        {/* 스캔 애니메이션 컨테이너 */}
        <div className="relative w-48 h-48 mx-auto">
          {/* 문서/단계 아이콘 */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-7xl">{displayIcon}</span>
          </div>

          {/* 스캔 라인 애니메이션 */}
          <div
            className="absolute left-0 right-0 h-1 bg-[#9bbc0f] opacity-80"
            style={{
              animation: 'scanLine 2s ease-in-out infinite',
              boxShadow: '0 0 10px #9bbc0f, 0 0 20px #9bbc0f',
            }}
          />

          {/* 돋보기 아이콘 (회전 애니메이션) */}
          <div
            className="absolute top-1/4 right-1/4"
            style={{
              animation: 'pulse 1s ease-in-out infinite',
            }}
          >
            <span className="text-3xl">🔍</span>
          </div>
        </div>

        {/* 로딩 메시지 */}
        <div className="space-y-2">
          <p className="font-pixel text-lg text-[#9bbc0f]">
            {displayMessage}
          </p>

          {/* 진행률 바 (Phase 5) */}
          {stage && (
            <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-[#9bbc0f] transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* 프로그레스 도트 */}
          <div className="flex justify-center space-x-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-3 h-3 bg-[#9bbc0f] rounded-sm"
                style={{
                  animation: `blink 1.4s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
        </div>

        {/* 재시도 횟수 표시 (Phase 5) */}
        {retryCount > 0 && (
          <p className="font-pixel text-sm text-yellow-400">
            ⚠️ 재시도 중... ({retryCount}회)
          </p>
        )}

        {/* 타임아웃 경고 (Phase 5) */}
        {showTimeoutWarning && (
          <div className="bg-yellow-900/50 border border-yellow-600 rounded-lg p-3">
            <p className="font-pixel text-sm text-yellow-400">
              ⏰ 예상보다 오래 걸리고 있습니다
            </p>
            <p className="font-pixel text-xs text-yellow-500 mt-1">
              네트워크 상태를 확인해주세요
            </p>
          </div>
        )}

        {/* 하단 안내 및 취소 버튼 */}
        <div className="space-y-3">
          <p className="font-pixel text-xs text-gray-500">
            잠시만 기다려주세요 (약 5-10초)
          </p>

          {/* 취소 버튼 (Phase 5) */}
          {onCancel && (
            <button
              onClick={onCancel}
              className="font-pixel text-sm text-gray-400 hover:text-white
                         border border-gray-600 hover:border-gray-400
                         px-4 py-2 rounded transition-colors"
            >
              ✕ 취소하기
            </button>
          )}
        </div>

        {/* 경과 시간 표시 (디버깅용, 25초 이후에만) */}
        {showTimeoutWarning && (
          <p className="font-pixel text-xs text-gray-600">
            경과: {Math.floor(elapsedTime / 1000)}초
          </p>
        )}
      </div>

      {/* 커스텀 애니메이션 스타일 */}
      <style>{`
        @keyframes scanLine {
          0%, 100% {
            top: 10%;
            opacity: 0.3;
          }
          50% {
            top: 85%;
            opacity: 1;
          }
        }

        @keyframes blink {
          0%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }
      `}</style>
    </div>
  );
};

VisionLoadingOverlay.displayName = 'VisionLoadingOverlay';
